export const TASK_STATUS = {
  AVAILABLE: "available",
  CLAIMED: "claimed",
  COMPLETED: "completed",
  EXPIRED: "expired",
  DRAFT: "draft",
} as const;

export type TaskStatusValue = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];

export const RESULT_STATUS = {
  DRAFT: "draft",
  SUBMITTED: "submitted",
} as const;

export type ResultStatusValue = (typeof RESULT_STATUS)[keyof typeof RESULT_STATUS];

export interface AuditUseCase {
  id: string;
  name: string;
  inter_rater_count?: number;
  [key: string]: unknown;
}

export interface AuditTask {
  id: string;
  use_case_id: string;
  status: TaskStatusValue;
  assigned_to: string | null;
  priority?: number;
  created_at: string;
  [key: string]: unknown;
}

export interface AuditResult {
  id: string;
  task_id: string;
  auditor_id: string;
  status: ResultStatusValue;
  decisions?: Record<string, { decision: string }>;
  [key: string]: unknown;
}

// ----------------------------  Config Enums  ----------------------------

export const USE_CASE_CATEGORY = {
  SAFETY_GENIE: "Safety Genie",
  SAFETY_VISION: "Safety Vision",
  CUSTOM: "Custom",
} as const;

export type UseCaseCategoryValue = (typeof USE_CASE_CATEGORY)[keyof typeof USE_CASE_CATEGORY];

export const DATA_SOURCE_TYPE = {
  TEXT_ONLY: "text_only",
  SINGLE_IMAGE_TEXT: "single_image_text",
  MULTI_IMAGE_TEXT: "multi_image_text",
  DOCUMENT_TEXT: "document_text",
  AUDIO_TEXT: "audio_text",
  VIDEO_TEXT: "video_text",
} as const;

export type DataSourceTypeValue = (typeof DATA_SOURCE_TYPE)[keyof typeof DATA_SOURCE_TYPE];

export const DATA_SOURCE_LABELS: Record<DataSourceTypeValue, string> = {
  text_only: "Text only",
  single_image_text: "Single image + text",
  multi_image_text: "Multiple images + text",
  document_text: "Document + text",
  audio_text: "Audio + text",
  video_text: "Video + text",
};

// Where a sampling run draws a use case's records from. The wire field is
// ``ingestion_source_type`` (see UseCaseConfig); these are its codes.
export const INGESTION_SOURCE_TYPE = {
  BATCH: "batch",
  TABLE: "table",
  S3_PATH: "s3_path",
} as const;

export type IngestionSourceTypeValue =
  (typeof INGESTION_SOURCE_TYPE)[keyof typeof INGESTION_SOURCE_TYPE];

export const INGESTION_SOURCE_LABELS: Record<IngestionSourceTypeValue, string> = {
  batch: "Batch",
  table: "Table",
  s3_path: "S3 path",
};

export const FIELD_TYPE = {
  TEXT_INPUT: "text_input",
  DROPDOWN_SINGLE: "dropdown_single",
  DROPDOWN_MULTI: "dropdown_multi",
  IMAGE_VIEWER: "image_viewer",
  DOCUMENT_VIEWER: "document_viewer",
  CHECKBOX: "checkbox",
  NUMERIC: "numeric",
  AUDIO_PLAYER: "audio_player",
  VIDEO_PLAYER: "video_player",
} as const;

export type FieldTypeValue = (typeof FIELD_TYPE)[keyof typeof FIELD_TYPE];

export const FIELD_TYPE_LABELS: Record<FieldTypeValue, string> = {
  text_input: "Text input",
  dropdown_single: "Dropdown (single)",
  dropdown_multi: "Dropdown (multi)",
  image_viewer: "Image viewer",
  document_viewer: "Document viewer",
  checkbox: "Checkbox",
  numeric: "Numeric",
  audio_player: "Audio player",
  video_player: "Video player",
};

export const OPTION_FIELD_TYPES: FieldTypeValue[] = [
  FIELD_TYPE.DROPDOWN_SINGLE,
  FIELD_TYPE.DROPDOWN_MULTI,
];

export const MEDIA_FIELD_TYPES: FieldTypeValue[] = [
  FIELD_TYPE.IMAGE_VIEWER,
  FIELD_TYPE.DOCUMENT_VIEWER,
  FIELD_TYPE.AUDIO_PLAYER,
  FIELD_TYPE.VIDEO_PLAYER,
];

// ----------------------------  Config Interfaces  ----------------------------

export interface FormConfigField {
  id: string;
  type: FieldTypeValue;
  label: string;
  options: string[];
  mandatory: boolean;
  help_text: string;
  display_order: number;
  default_value: string;
  multiline: boolean;
  allow_other: boolean;
  // Hover details per dropdown option, keyed by the option text.
  option_details?: Record<string, string>;
  // Heading of the correction asked for when the field is Incorrect
  // (blank = "Issue Category"), and its hover details.
  correction_label?: string;
  correction_help?: string;
  // Sample columns the reviewed value is read from; the first with a value is shown.
  // RDS column names (predicted_verdict) on a record; object presence records read
  // each object's JSON keys (predictedVerdict). A field with no value is hidden.
  source_columns?: string[];
}

// One pickable sample column (GET /audit_config/use_cases/source_columns). value is
// what source_columns stores: an RDS column (predicted_verdict, record level) or a
// path into the objects / objects_override JSON (objects.predictedVerdict, object level).
export interface SourceColumn {
  value: string;
  label: string;
  level: "record" | "object";
  type: "text" | "boolean" | "list" | "datetime";
}

// Buttons a checklist question offers unless configured otherwise.
export const DEFAULT_CHECKLIST_OPTIONS = ["Pass", "Fail", "N/A"];

export interface ChecklistItem {
  id: string;
  label: string;
  display_order: number;
  // hover details next to the question
  help_text?: string;
  // the buttons the auditor picks from; the picked label is stored
  options: string[];
}

// A read-only row of the additional view (Additional View tab): shown instead of the
// field rows where one of additional_view_enable_flags is true; nothing is answered on it.
// Shows the first of source_columns that has a value, a dash where none has one.
export interface AdditionalViewField {
  id: string;
  label: string;
  source_columns: string[];
  display_order: number;
}

export interface ReviewSampling {
  second_pct: number;
  third_pct: number;
  second_enabled: boolean;
  third_enabled: boolean;
  second_reviewer: string;
  third_reviewer: string;
}

export function makeReviewSampling(overrides: Partial<ReviewSampling> = {}): ReviewSampling {
  return {
    second_pct: 0,
    third_pct: 0,
    second_enabled: false,
    third_enabled: false,
    second_reviewer: "",
    third_reviewer: "",
    ...overrides,
  };
}

// One row of a use case's version history (GET /audit_config/use_cases/{id}/versions).
export type UseCaseVersionStatus = "Draft" | "Active" | "Archived";

export interface UseCaseVersionSummary {
  useCaseId: string;
  version: number;
  status: UseCaseVersionStatus;
  fieldCount: number;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

// Ingestion Sampling tab (mirrors the backend UseCaseConfigIngestionSampling).
export interface IngestionSampling {
  // share (%) of each site's records the daily sampling job draws (1-100); missing =
  // 100 (all). The manual sampling run uses its own percent input.
  sample_percent?: number;
  // site codes (upper case) never imported by the daily job, a manual run or a preview
  excluded_sites?: string[];
}

export interface UseCaseConfig {
  id: string;
  name: string;
  description: string;
  category: UseCaseCategoryValue;
  data_source_type: DataSourceTypeValue;
  // Ingestion tab. ``ingestion_source_type`` is the backend wire name for the
  // ingestion source *kind*; ``ingestion_source`` is the concrete pointer.
  ingestion_source_type: IngestionSourceTypeValue;
  ingestion_source: string;
  inter_rater_count: number;
  review_sampling: ReviewSampling;
  status: "active" | "inactive";
  fields: FormConfigField[];
  checklist_items: ChecklistItem[];
  // Where the checklist shows: only where one of checklist_enable_flags (boolean source
  // columns, e.g. is_human_override or objects.isHumanOverride) is true; none = every
  // record / object.
  checklist_enable_flags: string[];
  // Additional View tab: read-only rows shown instead of the fields on a record /
  // object where one of additional_view_enable_flags (boolean source columns) is true
  additional_view_fields?: AdditionalViewField[];
  additional_view_enable_flags?: string[];
  // Ingestion Sampling tab: daily job sample rate + sites never imported.
  ingestion_sampling?: IngestionSampling;
  // Auditors tab: share of cases per auditor. Only aliases with > 0% are stored;
  // when non-empty the shares must total 100.
  allocations?: UseCaseAllocation[];
  created_at: string;
  updated_at: string;
}

export interface UseCaseAllocation {
  login: string;
  percent: number;
}

// ----------------------------  Config Helpers  ----------------------------

export function configUid(prefix: string): string {
  return prefix + "_" + crypto.randomUUID().replace(/-/g, "").slice(0, 12);
}

export function makeFormConfigField(overrides: Partial<FormConfigField> = {}): FormConfigField {
  return {
    id: configUid("f"),
    type: FIELD_TYPE.TEXT_INPUT,
    label: "",
    options: [],
    mandatory: false,
    help_text: "",
    display_order: 0,
    default_value: "",
    multiline: false,
    allow_other: false,
    option_details: {},
    correction_label: "",
    correction_help: "",
    source_columns: [],
    ...overrides,
  };
}

export function makeChecklistItem(overrides: Partial<ChecklistItem> = {}): ChecklistItem {
  return {
    id: configUid("cl"),
    label: "",
    display_order: 0,
    help_text: "",
    options: [...DEFAULT_CHECKLIST_OPTIONS],
    ...overrides,
  };
}

export function makeAdditionalViewField(
  overrides: Partial<AdditionalViewField> = {},
): AdditionalViewField {
  return {
    id: configUid("dt"),
    label: "",
    source_columns: [""],
    display_order: 0,
    ...overrides,
  };
}

export function makeUseCaseConfig(overrides: Partial<UseCaseConfig> = {}): UseCaseConfig {
  const ts = new Date().toISOString();
  return {
    id: configUid("uc"),
    name: "",
    description: "",
    category: USE_CASE_CATEGORY.CUSTOM,
    data_source_type: DATA_SOURCE_TYPE.TEXT_ONLY,
    ingestion_source_type: INGESTION_SOURCE_TYPE.BATCH,
    ingestion_source: "",
    inter_rater_count: 1,
    review_sampling: makeReviewSampling(),
    status: "inactive",
    fields: [],
    checklist_items: [],
    checklist_enable_flags: [],
    additional_view_fields: [],
    ingestion_sampling: { sample_percent: 100, excluded_sites: [] },
    created_at: ts,
    updated_at: ts,
    ...overrides,
  };
}

// ----------------------------  Workspace Types  ----------------------------

export const SIMPLE_DECISIONS = ["correct", "incorrect"] as const;

export type SimpleDecisionValue = (typeof SIMPLE_DECISIONS)[number];
// A checklist answer: the label of the button picked (one of the item's options).
export type ChecklistResponseValue = string;

export interface MediaUrl {
  url: string;
  type: "image" | "document" | "pdf" | "audio" | "video";
  label: string;
}

export interface FieldDecision {
  decision: string;
  // Why an Incorrect field is wrong: the picked option, or "Other: <text>".
  issue_category?: string;
}

// Review-level values shared by all fields (open to more keys later).
export interface FieldCommon {
  // Optional Details note, shown while any field is incorrect.
  explanation?: string;
}

// One object's review on an object presence record: decisions keyed by field label
// plus the object's own Details note.
export interface ObjectReview {
  decisions: Record<string, FieldDecision>;
  field_common?: FieldCommon;
  // the object's checklist answers, keyed by checklist item id
  checklist_responses?: Record<string, ChecklistResponseValue>;
}

// One object's inspector images on an object presence record.
export interface ObjectMedia {
  override: MediaUrl[];
  fix: MediaUrl[];
}

export interface WorkspaceTask {
  id: string;
  use_case_id: string;
  status: TaskStatusValue;
  assigned_to: string | null;
  priority: number;
  created_at: string;
  week?: string;
  incident_description?: string;
  inspection_question?: string;
  // Untransformed sample record from the backend; mapped to fields in the UI.
  raw_values?: Record<string, unknown>;
  media_urls?: MediaUrl[];
  // the record's override images (the inspector's own photos)
  override_media_urls?: MediaUrl[];
  // objectName -> its override / compliance fix images (only objects with any)
  object_media?: Record<string, ObjectMedia>;
}

export interface WorkspaceResult {
  id: string;
  task_id: string;
  auditor_id: string;
  status: ResultStatusValue;
  decisions: Record<string, FieldDecision>;
  // Absent on results saved before field_common existed.
  field_common?: FieldCommon;
  checklist_responses: Record<string, ChecklistResponseValue>;
  comments: string;
  // Object presence records only: objectName -> that object's review.
  object_reviews?: Record<string, ObjectReview>;
  // Comments-section flag for data or guideline follow-up.
  is_flagged?: boolean;
}

// ----------------------------  Permissions  ----------------------------

export const ROLE = {
  AUDITOR: "auditor",
  OWNER: "owner",
  ADMIN: "admin",
} as const;

export type RoleValue = (typeof ROLE)[keyof typeof ROLE];

// Roles that can be assigned from the UI. Admin is the root role and is
// granted out-of-band (LDAP audit-admin / server config), never from the front end.
export const ASSIGNABLE_ROLES: RoleValue[] = [ROLE.AUDITOR, ROLE.OWNER];

export const ROLE_LABELS: Record<RoleValue, string> = {
  [ROLE.AUDITOR]: "Auditor",
  [ROLE.OWNER]: "Program Owner",
  [ROLE.ADMIN]: "Admin",
};

export interface PermissionsMember {
  login: string;
  name: string;
  role: RoleValue;
  assigned_use_case_ids: string[];
  // Elevated Admin: authorized on every use case implicitly (server-resolved).
  elevated?: boolean;
  // Roles the caller may set on this member (server-resolved). Empty means the
  // caller cannot manage this member (e.g. an Admin row, or out of scope).
  assignable_roles?: RoleValue[];
}

// ROLE value for a backend PermissionRole string ('Admin' | 'Program Owner' | 'Auditor').
export const ROLE_BY_API: Record<string, RoleValue> = {
  Auditor: ROLE.AUDITOR,
  "Program Owner": ROLE.OWNER,
  Admin: ROLE.ADMIN,
};

export const API_ROLE: Record<RoleValue, string> = {
  [ROLE.AUDITOR]: "Auditor",
  [ROLE.OWNER]: "Program Owner",
  [ROLE.ADMIN]: "Admin",
};
